import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell, TableFooter } from './chunk-PEIGN6MO.js';
import { Badge } from './chunk-S775MXMS.js';
import { cn } from './chunk-FFX3LZW7.js';
import { QuotaStatus, evaluateQuota, quotaUsedFraction, resolvePlanById, grantedEntitlements, hasEntitlement, attributionPath, rollupUsage, groupUsageBy, isCreditEvent, signedQuantity, isBillable } from '@authe/fe-commons/plans';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import * as React from 'react';
import { resolveLoadProfile, budgetFor } from '@authe/fe-commons/budgets';
import { formatRelativeTime } from '@authe/fe-commons/time';

// src/quota/format.ts
function compactNumber(n) {
  return new Intl.NumberFormat(void 0, {
    notation: "compact",
    maximumFractionDigits: 1
  }).format(n);
}
var FILL_BY_STATUS = {
  [QuotaStatus.ALLOW]: "bg-brand",
  [QuotaStatus.WARN]: "bg-risk-medium",
  [QuotaStatus.THROTTLE]: "bg-risk-high",
  [QuotaStatus.REJECT]: "bg-risk-critical"
};
var STATUS_LABEL = {
  [QuotaStatus.ALLOW]: "OK",
  [QuotaStatus.WARN]: "Near limit",
  [QuotaStatus.THROTTLE]: "Throttled",
  [QuotaStatus.REJECT]: "Over limit"
};
function QuotaMeter(props) {
  const { decision, policy, showCounts = true, className } = props;
  const used = decision?.used ?? props.used ?? 0;
  const limit = decision?.limit ?? policy?.limit ?? props.limit ?? 0;
  let status;
  if (decision) status = decision.status;
  else if (props.status) status = props.status;
  else if (policy) status = evaluateQuota(policy, used).status;
  else status = used >= limit && limit > 0 ? QuotaStatus.REJECT : QuotaStatus.ALLOW;
  const unbounded = !limit || limit <= 0;
  const fraction = quotaUsedFraction(used, limit);
  const percent = Math.min(100, Math.round(fraction * 100));
  const label = props.label ?? decision?.policy_id ?? policy?.id ?? "Usage";
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-1.5", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2 text-sm", children: [
      /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: label }),
      /* @__PURE__ */ jsx(Badge, { variant: "outline", className: cn("border-transparent", FILL_BY_STATUS[status], "text-white"), children: STATUS_LABEL[status] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "h-2 w-full overflow-hidden rounded-full bg-secondary", children: /* @__PURE__ */ jsx(
      "div",
      {
        className: cn("h-full rounded-full transition-all", FILL_BY_STATUS[status]),
        style: { width: `${unbounded ? 0 : percent}%` }
      }
    ) }),
    showCounts && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsxs("span", { children: [
        compactNumber(used),
        " used"
      ] }),
      /* @__PURE__ */ jsx("span", { children: unbounded ? "Unlimited" : `${compactNumber(limit)} limit \xB7 ${percent}%` })
    ] })
  ] });
}
var ACCENT_BY_ID = {
  free: "bg-secondary text-secondary-foreground",
  pro: "bg-brand text-brand-foreground",
  enterprise: "bg-finality-finalized text-white"
};
function PlanBadge({ plan, planId, className }) {
  const resolved = plan ?? resolvePlanById(planId);
  const accent = ACCENT_BY_ID[resolved.id] ?? "bg-primary text-primary-foreground";
  return /* @__PURE__ */ jsx(Badge, { variant: "outline", className: cn("border-transparent", accent, className), children: resolved.display_name });
}
var UNKNOWN = { plan: null, granted: null, profile: "standard" };
var PlanContext = React.createContext(UNKNOWN);
function PlanProvider({
  plan,
  overrides,
  subscriptionMetadata,
  children
}) {
  const value = React.useMemo(
    () => ({
      plan: plan ?? null,
      granted: plan ? grantedEntitlements(plan, overrides ?? {}) : null,
      profile: resolveLoadProfile(plan?.metadata ?? null, subscriptionMetadata ?? null)
    }),
    [plan, overrides, subscriptionMetadata]
  );
  return /* @__PURE__ */ jsx(PlanContext.Provider, { value, children });
}
function usePlan() {
  return React.useContext(PlanContext);
}
function useEntitlement(entitlement) {
  const { granted } = usePlan();
  return granted === null || granted.has(entitlement);
}
function useEntitled() {
  const { granted } = usePlan();
  return React.useCallback(
    (entitlement) => !entitlement || granted === null || granted.has(entitlement),
    [granted]
  );
}
function useLoadBudget(name) {
  const { profile } = usePlan();
  return React.useMemo(() => budgetFor(name, profile), [name, profile]);
}
function EntitlementGate({
  plan,
  entitlement,
  children,
  fallback = null
}) {
  const context = usePlan();
  const included = plan ? hasEntitlement(plan, entitlement) : context.granted === null || context.granted.has(entitlement);
  return /* @__PURE__ */ jsx(Fragment, { children: included ? children : fallback });
}
var KIND_LABEL = {
  per_tx: "Transactions",
  per_provider_call: "Provider calls",
  per_step: "Steps",
  per_enrichment: "Enrichments",
  credit: "Credits"
};
function kindLabel(kind) {
  return KIND_LABEL[kind] ?? kind;
}
function AttributionPath({
  event,
  className
}) {
  const path = attributionPath(event);
  if (path.length === 0) return null;
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn("inline-flex flex-wrap items-center gap-1 text-xs text-muted-foreground", className),
      "aria-label": `Attributed to ${path.map((p) => `${p.rung} ${p.id}`).join(", ")}`,
      children: path.map((segment, index) => /* @__PURE__ */ jsxs(React.Fragment, { children: [
        index > 0 ? /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: "\u203A" }) : null,
        /* @__PURE__ */ jsx("span", { title: segment.rung, className: "font-mono", children: segment.id })
      ] }, `${segment.rung}:${segment.id}`))
    }
  );
}
function UsageSummary(props) {
  const {
    label = "Usage",
    showBreakdown = false,
    attribution = null,
    emptyLabel = "No usage recorded",
    className
  } = props;
  const rollup = props.rollup ?? rollupUsage(props.events ?? []);
  const isEmpty = rollup.events === 0;
  const breakdown = React.useMemo(
    () => Object.entries(rollup.byKind).filter(([, qty]) => qty !== 0).sort((a, b) => b[1] - a[1]),
    [rollup.byKind]
  );
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-baseline justify-between gap-3", children: [
      /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: label }),
      rollup.credits > 0 ? (
        // Surfaced rather than left to net silently: a reversed charge and no
        // activity at all produce the same total, and they are not the same.
        /* @__PURE__ */ jsxs(Badge, { variant: "outline", title: "Reorg credits reversing earlier charges", children: [
          rollup.credits,
          " credited"
        ] })
      ) : null
    ] }),
    isEmpty ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: emptyLabel }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-baseline gap-2", children: [
        /* @__PURE__ */ jsx("span", { className: "text-2xl font-semibold tabular-nums", children: compactNumber(rollup.billable) }),
        /* @__PURE__ */ jsx("span", { className: "text-sm text-muted-foreground", children: "billable" }),
        rollup.telemetry > 0 ? /* @__PURE__ */ jsxs(
          "span",
          {
            className: "text-xs text-muted-foreground",
            title: "Recorded for observability; not charged",
            children: [
              "+",
              compactNumber(rollup.telemetry),
              " measured"
            ]
          }
        ) : null
      ] }),
      showBreakdown && breakdown.length > 0 ? /* @__PURE__ */ jsx("dl", { className: "flex flex-col gap-1", children: breakdown.map(([kind, qty]) => /* @__PURE__ */ jsxs("div", { className: "flex items-baseline justify-between gap-3 text-sm", children: [
        /* @__PURE__ */ jsx("dt", { className: "text-muted-foreground", children: kindLabel(kind) }),
        /* @__PURE__ */ jsx("dd", { className: "font-mono tabular-nums", children: compactNumber(qty) })
      ] }, kind)) }) : null
    ] }),
    attribution ? /* @__PURE__ */ jsx(AttributionPath, { event: attribution }) : null
  ] });
}
var RUNG_HEADING = {
  customer: "Customer",
  org: "Organization",
  tenant: "Tenant",
  user: "User",
  actor: "Actor"
};
function UsageBreakdown({
  events,
  groups,
  rung,
  limit,
  emptyLabel = "No usage recorded in this period",
  renderKey,
  className
}) {
  const rows = React.useMemo(
    () => groups ? [...groups] : groupUsageBy(events ?? [], rung),
    [groups, events, rung]
  );
  const total = React.useMemo(
    () => rows.reduce((sum, r) => sum + r.rollup.billable, 0),
    [rows]
  );
  if (rows.length === 0) {
    return /* @__PURE__ */ jsx("p", { className: cn("text-sm text-muted-foreground", className), children: emptyLabel });
  }
  const shown = limit != null ? rows.slice(0, limit) : rows;
  const hidden = rows.length - shown.length;
  return /* @__PURE__ */ jsx("div", { className: cn("overflow-x-auto", className), children: /* @__PURE__ */ jsxs(Table, { className: "w-full text-sm", children: [
    /* @__PURE__ */ jsx(TableHeader, { children: /* @__PURE__ */ jsxs(TableRow, { className: "border-b text-left text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsx(TableHead, { scope: "col", className: "py-2 pr-3 font-medium", children: RUNG_HEADING[rung] }),
      /* @__PURE__ */ jsx(TableHead, { scope: "col", className: "py-2 pr-3 text-right font-medium", children: "Billable" }),
      /* @__PURE__ */ jsx(TableHead, { scope: "col", className: "py-2 pr-3 text-right font-medium", children: "Measured" }),
      /* @__PURE__ */ jsx(TableHead, { scope: "col", className: "py-2 text-right font-medium", children: "Share" })
    ] }) }),
    /* @__PURE__ */ jsx(TableBody, { children: shown.map((row) => {
      const share = total > 0 ? row.rollup.billable / total : 0;
      return /* @__PURE__ */ jsxs(TableRow, { className: "border-b last:border-0", children: [
        /* @__PURE__ */ jsxs(TableCell, { className: "py-2 pr-3", children: [
          /* @__PURE__ */ jsx("span", { className: "font-mono text-xs", children: renderKey ? renderKey(row.key) : row.key }),
          row.rollup.credits > 0 ? /* @__PURE__ */ jsxs(
            "span",
            {
              className: "ml-2 text-xs text-muted-foreground",
              title: "Reorg credits reversing earlier charges",
              children: [
                row.rollup.credits,
                " credited"
              ]
            }
          ) : null
        ] }),
        /* @__PURE__ */ jsx(TableCell, { className: "py-2 pr-3 text-right font-medium tabular-nums", children: compactNumber(row.rollup.billable) }),
        /* @__PURE__ */ jsx(TableCell, { className: "py-2 pr-3 text-right tabular-nums text-muted-foreground", children: compactNumber(row.rollup.telemetry) }),
        /* @__PURE__ */ jsx(TableCell, { className: "py-2 text-right tabular-nums text-muted-foreground", children: total > 0 ? `${Math.round(share * 100)}%` : "\u2014" })
      ] }, row.key);
    }) }),
    hidden > 0 ? /* @__PURE__ */ jsx(TableFooter, { children: /* @__PURE__ */ jsx(TableRow, { className: "text-xs text-muted-foreground", children: /* @__PURE__ */ jsxs(TableCell, { className: "py-2 pr-3", colSpan: 4, children: [
      "+",
      hidden,
      " more ",
      hidden === 1 ? "row" : "rows"
    ] }) }) }) : null
  ] }) });
}
var KIND_LABEL2 = {
  per_tx: "Transaction",
  per_provider_call: "Provider call",
  per_step: "Step",
  per_enrichment: "Enrichment",
  credit: "Credit"
};
function kindLabel2(kind) {
  return KIND_LABEL2[kind] ?? kind;
}
function occurredAtMs(e) {
  const ms = Date.parse(e.occurred_at);
  return Number.isNaN(ms) ? null : ms;
}
var numberFormat = new Intl.NumberFormat();
function UsageEventRow({
  event,
  showAttribution,
  onOpenTransaction
}) {
  const ms = occurredAtMs(event);
  const credit = isCreditEvent(event);
  const quantity = signedQuantity(event);
  return /* @__PURE__ */ jsxs("li", { className: "flex flex-col gap-1 border-b py-2 last:border-0", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-wrap items-center gap-2", children: [
        /* @__PURE__ */ jsx(Badge, { variant: credit ? "outline" : "secondary", children: kindLabel2(event.kind) }),
        event.subkind ? /* @__PURE__ */ jsx("span", { className: "font-mono text-xs text-muted-foreground", children: event.subkind }) : null,
        !isBillable(event) && !credit ? /* @__PURE__ */ jsx(Badge, { variant: "outline", title: "Recorded for observability; not charged", children: "measured" }) : null,
        event.tx_id ? onOpenTransaction ? /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "max-w-[16rem] truncate font-mono text-xs text-muted-foreground underline-offset-2 hover:underline",
            onClick: () => onOpenTransaction(event.tx_id),
            title: event.tx_id,
            children: event.tx_id
          }
        ) : /* @__PURE__ */ jsx(
          "span",
          {
            className: "max-w-[16rem] truncate font-mono text-xs text-muted-foreground",
            title: event.tx_id,
            children: event.tx_id
          }
        ) : null
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-3", children: [
        /* @__PURE__ */ jsxs(
          "span",
          {
            className: cn(
              "font-medium tabular-nums",
              credit ? "text-muted-foreground" : "text-foreground"
            ),
            children: [
              quantity > 0 ? "+" : "",
              numberFormat.format(quantity)
            ]
          }
        ),
        ms != null ? /* @__PURE__ */ jsx(
          "span",
          {
            className: "text-xs text-muted-foreground",
            title: new Date(ms).toLocaleString(),
            children: formatRelativeTime(ms, { unit: "milliseconds" })
          }
        ) : null
      ] })
    ] }),
    showAttribution ? /* @__PURE__ */ jsx(AttributionPath, { event }) : null
  ] });
}
function UsageEventList({
  events,
  limit,
  showAttribution = true,
  emptyLabel = "No usage recorded in this period",
  onOpenTransaction,
  className
}) {
  if (events.length === 0) {
    return /* @__PURE__ */ jsx("p", { className: cn("text-sm text-muted-foreground", className), children: emptyLabel });
  }
  const shown = limit != null ? events.slice(0, limit) : events;
  const hidden = events.length - shown.length;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    /* @__PURE__ */ jsx("ul", { className: "flex flex-col", children: shown.map((event) => /* @__PURE__ */ jsx(
      UsageEventRow,
      {
        event,
        showAttribution,
        onOpenTransaction
      },
      event.id
    )) }),
    hidden > 0 ? /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
      "+",
      hidden,
      " more ",
      hidden === 1 ? "unit" : "units",
      " in this period"
    ] }) : null
  ] });
}

export { AttributionPath, EntitlementGate, PlanBadge, PlanProvider, QuotaMeter, UsageBreakdown, UsageEventList, UsageSummary, compactNumber, useEntitled, useEntitlement, useLoadBudget, usePlan };
//# sourceMappingURL=chunk-XAZGJ5PW.js.map
//# sourceMappingURL=chunk-XAZGJ5PW.js.map